import { useEffect, useState } from 'react';
import { MessagesSquare, UsersRound } from 'lucide-react';
import { ComplianceRefusal, conversationsApi } from '../api/conversations';
import { rosterApi } from '../api/auth';
import type { RosterRow } from '../api/auth';
import { subgroupsApi } from '../api/subgroups';
import type { Subgroup } from '../api/subgroups';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { RefusalCard } from '../ui/ComplianceNotice';
import { SelectField, TextField } from '../ui/Field';
import { Avatar, ParticipantLegend } from '../ui/ParticipantChip';

type Audience = 'unit' | 'subgroup' | 'adults' | 'pick';

export function NewConversationScreen({
  unitId,
  subgroupLabelSingular,
  subgroupLabelPlural,
  canCreateForRoster,
  onCreated,
  onCancel,
}: {
  unitId: string;
  /** The unit's own word. Every mention of a group on this screen renders from these two. */
  subgroupLabelSingular: string;
  subgroupLabelPlural: string;
  canCreateForRoster: boolean;
  onCreated: (conversationId: string) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [audience, setAudience] = useState<Audience>(canCreateForRoster ? 'unit' : 'pick');
  const [subgroups, setSubgroups] = useState<Subgroup[]>([]);
  const [subgroupId, setSubgroupId] = useState('');
  const [roster, setRoster] = useState<RosterRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [refusal, setRefusal] = useState<{ title: string; detail: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    rosterApi
      .list(unitId)
      .then(setRoster)
      .catch(() => setError('Could not load the roster.'));

    // Retired ones are not offered: a thread scoped to a group that no longer exists would arrive
    // with nobody in it, which is precisely the state this audience used to produce for every
    // group, live or not.
    subgroupsApi
      .list(unitId)
      .then((all) => setSubgroups(all.filter((sg) => !sg.isRetired)))
      .catch(() => undefined);
  }, [unitId]);

  function toggle(personId: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(personId)) next.delete(personId);
      else next.add(personId);
      return next;
    });
  }

  // Built here rather than as a module constant, because two of the four labels are the unit's
  // own word for its groups. A constant would have to hard-code "Patrol".
  const audiences: { value: Audience; label: string; detail: string }[] = [
    {
      value: 'unit',
      label: 'Everyone in the unit',
      detail: 'All members. Leaders are present automatically.',
    },
    {
      value: 'subgroup',
      label: `One ${subgroupLabelSingular.toLowerCase()}`,
      detail:
        `Its members, plus the unit\u2019s adult leaders as monitors \u2014 so it satisfies the ` +
        `two-adult rule without anyone adding an adult.`,
    },
    {
      value: 'adults',
      label: 'Adults only',
      detail: 'Leaders and parents. No youth, so youth protection rules don\u2019t apply.',
    },
    {
      value: 'pick',
      label: 'Choose people',
      detail: 'Pick who\u2019s in it. If any youth are included, two adults are required.',
    },
  ];

  // Mirrors the server rule so the UI can warn before submitting — but the server
  // is still the enforcement point, and a refusal from it is rendered as-is.
  const chosen = roster.filter((r) => selected.has(r.personId));
  const youthChosen = chosen.filter((r) => r.classification === 'Youth').length;
  const adultsChosen = chosen.filter((r) => r.classification === 'Adult').length;
  const quorumShort = audience === 'pick' && youthChosen > 0 && adultsChosen < 2;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setRefusal(null);
    setError(null);

    try {
      const result = await conversationsApi.create(unitId, {
        title: title.trim(),
        audienceMode: audience === 'pick' ? 'Explicit' : 'RosterScoped',
        audienceIncludes: audience === 'adults' ? 'AdultsOnly' : 'Everyone',
        audienceSubgroupId: audience === 'subgroup' ? subgroupId : undefined,
        participantIds: audience === 'pick' ? [...selected] : undefined,
      });
      onCreated(result.conversationId);
    } catch (e) {
      if (e instanceof ComplianceRefusal) {
        setRefusal({ title: e.title, detail: e.message });
      } else {
        setError(e instanceof Error ? e.message : 'Could not start the conversation.');
      }
      setBusy(false);
    }
  }

  return (
    <Page>
      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p
              className="t-eyebrow inline-flex items-center gap-2"
              style={{ color: 'var(--color-accent-announcement-text)', margin: 0 }}
            >
              <MessagesSquare size={14} aria-hidden="true" />
              New conversation
            </p>
            <h1
              className="t-heading"
              style={{ color: 'var(--color-text-primary)', margin: 'var(--space-2) 0 0' }}
            >
              Who is this for?
            </h1>
          </div>
        </Gutter>
      </section>

      <section className="flex-1" style={{ background: 'var(--color-surface-sunken)' }}>
        <Gutter>
          <form onSubmit={submit} style={{ paddingBlock: 'var(--space-6)', maxWidth: '40rem' }}>
            <div className="flex flex-col" style={{ gap: 'var(--space-5)' }}>
              <TextField
                label="Name"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Fall Camporee"
              />

              <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
                <legend className="t-label" style={{ color: 'var(--color-text-muted)' }}>
                  Audience
                </legend>

                <div className="flex flex-col" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
                  {audiences.map((option) => {
                    // A unit with no groups yet has nothing to scope a thread to, and the reason
                    // below says so rather than leaving a dead option on the form.
                    const disabled =
                      (option.value !== 'pick' && !canCreateForRoster) ||
                      (option.value === 'subgroup' && subgroups.length === 0);

                    return (
                      <label
                        key={option.value}
                        className="flex items-start gap-3"
                        style={{
                          background: 'var(--color-surface-raised)',
                          border:
                            audience === option.value
                              ? '1.5px solid var(--color-brand-ink)'
                              : '1.5px solid var(--color-border-subtle)',
                          borderRadius: 'var(--radius-md)',
                          padding: 'var(--space-3) var(--space-4)',
                          minHeight: 'var(--tap-min)',
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          opacity: disabled ? 0.55 : 1,
                        }}
                      >
                        <input
                          type="radio"
                          name="audience"
                          value={option.value}
                          checked={audience === option.value}
                          disabled={disabled}
                          onChange={() => setAudience(option.value)}
                          style={{ marginTop: '4px' }}
                        />
                        <span>
                          <span
                            className="t-body-strong"
                            style={{ color: 'var(--color-text-primary)', display: 'block' }}
                          >
                            {option.label}
                          </span>
                          <span
                            className="t-caption"
                            style={{ color: 'var(--color-text-secondary)' }}
                          >
                            {!disabled
                              ? option.detail
                              : option.value === 'subgroup' && subgroups.length === 0
                                ? `This unit has no ${subgroupLabelPlural.toLowerCase()} yet.`
                                : 'Only officers and admins can start a conversation for the whole roster.'}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              {audience === 'subgroup' && subgroups.length > 0 && (
                <SelectField
                  label={`Which ${subgroupLabelSingular.toLowerCase()}`}
                  required
                  value={subgroupId}
                  onChange={(e) => setSubgroupId(e.currentTarget.value)}
                >
                  <option value="">Choose one...</option>
                  {subgroups.map((sg) => (
                    <option key={sg.id} value={sg.id}>
                      {sg.name} ({sg.members.length})
                    </option>
                  ))}
                </SelectField>
              )}

              {audience === 'pick' && (
                <div>
                  <p className="t-label" style={{ color: 'var(--color-text-muted)', margin: '0 0 var(--space-2)' }}>
                    People
                  </p>

                  <div style={{ marginBottom: 'var(--space-3)' }}>
                    <ParticipantLegend />
                  </div>

                  <div className="flex flex-wrap" style={{ gap: '7px' }}>
                    {roster.map((row) => {
                      const isAdult = row.classification === 'Adult';
                      const on = selected.has(row.personId);

                      return (
                        <button
                          key={row.personId}
                          type="button"
                          onClick={() => toggle(row.personId)}
                          aria-pressed={on}
                          className="inline-flex items-center gap-2 t-caption"
                          style={{
                            minHeight: 'var(--tap-min)',
                            padding: '0 var(--space-3) 0 var(--space-1)',
                            // Shape carries classification; selection is carried by fill,
                            // so the two never compete for the same signal.
                            borderRadius: isAdult ? 'var(--radius-full)' : '8px',
                            border: isAdult
                              ? '1.5px solid var(--color-brand-ink)'
                              : '1.5px dashed var(--color-border-strong)',
                            background: on ? 'var(--color-surface-inverse)' : 'var(--color-surface-raised)',
                            color: on ? 'var(--color-text-on-inverse)' : 'var(--color-text-primary)',
                          }}
                        >
                          <Avatar
                            initials={row.displayName.split(' ').map((p) => p[0]).slice(0, 2).join('')}
                            classification={isAdult ? 'adult' : 'youth'}
                          />
                          {row.displayName}
                          <span className="sr-only">{isAdult ? '(adult)' : '(youth)'}</span>
                        </button>
                      );
                    })}
                  </div>

                  {youthChosen > 0 && (
                    <p
                      className="t-caption"
                      style={{
                        color: quorumShort
                          ? 'var(--color-status-blocked)'
                          : 'var(--color-status-compliant)',
                        marginTop: 'var(--space-3)',
                      }}
                    >
                      <UsersRound size={14} aria-hidden="true" style={{ verticalAlign: '-2px' }} />{' '}
                      {quorumShort
                        ? `${adultsChosen} adult${adultsChosen === 1 ? '' : 's'} selected — threads with youth need two.`
                        : `${adultsChosen} adults present.`}
                    </p>
                  )}
                </div>
              )}

              {refusal && (
                <RefusalCard
                  headline={refusal.title}
                  explanation={refusal.detail}
                  fixLabel="Choose someone else"
                  onFix={() => setRefusal(null)}
                />
              )}

              {error && (
                <p
                  className="t-body"
                  role="alert"
                  style={{
                    background: 'var(--color-status-blocked-surface)',
                    border: '1px solid var(--color-status-blocked)',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--space-3)',
                    color: 'var(--color-text-primary)',
                    margin: 0,
                  }}
                >
                  {error}
                </p>
              )}

              <div className="flex flex-wrap gap-3">
                <Button
                  variant="primary"
                  type="submit"
                  disabledReason={
                    !title.trim()
                      ? 'Give the conversation a name.'
                      : audience === 'subgroup' && !subgroupId
                        ? `Choose which ${subgroupLabelSingular.toLowerCase()}.`
                        : audience === 'pick' && selected.size === 0
                        ? 'Choose at least one person.'
                        : quorumShort
                          ? 'Add a second adult before youth can be included.'
                          : undefined
                  }
                >
                  {busy ? 'Starting…' : 'Start conversation'}
                </Button>

                <Button variant="secondary" onClick={onCancel}>
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        </Gutter>
      </section>

      <Footer />
    </Page>
  );
}
